import { Component, OnDestroy, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Subscription, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { CustomsCalculationsService, CustomsCalculation } from '../../../services/api/customs-calculations';
import { DealService, CargoDealDetail } from '../../../services/api/deal';
import { LanguageService } from '../../../services/i18n/language';
import { DocNumberPipe } from '../../../services/request-number';
import { CurrencyRatesService } from '../../../services/currency-rates';
import { ProcessCalculationComponent } from '../../public/process-calculation/process-calculation';
import { ViewDealComponent } from '../../shared/view-deal/view-deal';

/** Вкладки поставки — по порядку работы с ней. */
export type SupplyTab = 'calculation' | 'supplier' | 'transport' | 'certification' | 'customs' | 'summary';

const TABS: { id: SupplyTab; key: string; fallback: string }[] = [
  { id: 'calculation', key: 'coSupply.tabCalculation', fallback: 'Предварительный расчёт' },
  { id: 'supplier', key: 'coSupply.tabSupplier', fallback: 'Поставщик' },
  { id: 'transport', key: 'coSupply.tabTransport', fallback: 'Перевозка' },
  { id: 'certification', key: 'coSupply.tabCertification', fallback: 'Сертификация' },
  { id: 'customs', key: 'coSupply.tabCustoms', fallback: 'Таможенная очистка' },
  { id: 'summary', key: 'coSupply.tabSummary', fallback: 'Сводка по поставке' }
];

/**
 * Поставка грузовладельца (расчёт на сопровождении): вкладки сверху, меню кабинета — слева.
 * Открытые вкладки не пересоздаются при переключении — незаполненные формы (например, запрос на перевозку) не теряются.
 */
@Component({
  selector: 'app-co-supply',
  standalone: true,
  imports: [CommonModule, RouterModule, DocNumberPipe, ProcessCalculationComponent, ViewDealComponent],
  templateUrl: './co-supply.component.html',
  styleUrl: './co-supply.component.scss'
})
export class CoSupplyComponent implements OnInit, OnDestroy {
  readonly tabs = TABS;

  id = 0;
  /** Поставка по сделке без расчёта (запрос создавали не из расчёта): id сделки вместо id расчёта. */
  byDeal = false;
  dealId = 0;
  tab: SupplyTab = 'calculation';
  /** Вкладки, которые уже открывали: держим их на странице скрытыми. */
  visited = new Set<SupplyTab>();
  /** Курсы на сегодня — для прогноза доставки в тенге (как в самом расчёте). */
  private rates: Record<string, number> = {};

  calculation: CustomsCalculation | null = null;
  deal: CargoDealDetail | null = null;
  loading = true;
  error: string | null = null;

  private subscriptions = new Subscription();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private calculationsService: CustomsCalculationsService,
    private dealService: DealService,
    private languageService: LanguageService,
    private currencyRates: CurrencyRatesService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.subscriptions.add(this.languageService.getTranslations().subscribe(() => this.cdr.markForCheck()));
    this.subscriptions.add(
      this.route.paramMap.subscribe(params => {
        this.byDeal = !!this.route.snapshot.data['byDeal'];
        const tab = (params.get('tab') || 'calculation') as SupplyTab;
        this.tab = TABS.some(t => t.id === tab) ? tab : 'calculation';
        if (this.byDeal) {
          const dealId = +(params.get('dealId') || 0);
          if (dealId && dealId !== this.dealId) {
            this.dealId = dealId;
            this.visited.clear();
            this.loadDeal();
          }
        } else {
          const id = +(params.get('id') || 0);
          if (id && id !== this.id) {
            this.id = id;
            this.visited.clear();
            this.load();
          }
        }
        this.visited.add(this.tab);
        this.cdr.markForCheck();
        // На телефоне полоса вкладок прокручивается — показываем активную
        setTimeout(() => document.querySelector('.sp-tab.is-active')?.scrollIntoView({ block: 'nearest', inline: 'nearest' }));
      })
    );
    this.subscriptions.add(this.currencyRates.getRates().subscribe(r => {
      this.rates = { ...r };
      this.cdr.markForCheck();
    }));
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  t(key: string, fallback: string): string {
    const value = this.languageService.translate(key);
    return value && value !== key ? value : fallback;
  }

  private load(): void {
    this.loading = true;
    this.error = null;
    this.calculation = null;
    this.deal = null;
    this.subscriptions.add(
      this.calculationsService.getCalculationById(this.id).subscribe({
        next: calc => {
          this.calculation = calc;
          this.loading = false;
          const dealId = (calc.cargoRequests || []).find(r => r.dealId)?.dealId;
          if (dealId) {
            this.subscriptions.add(
              this.dealService.getDealDetail(dealId).pipe(catchError(() => of(null))).subscribe(d => {
                this.deal = d;
                this.cdr.markForCheck();
              })
            );
          }
          this.cdr.markForCheck();
        },
        error: () => {
          this.error = this.t('coSupply.loadError', 'Не удалось загрузить поставку');
          this.loading = false;
          this.cdr.markForCheck();
        }
      })
    );
  }

  /** Сделка без расчёта: только сама сделка. */
  private loadDeal(): void {
    this.loading = true;
    this.error = null;
    this.calculation = null;
    this.deal = null;
    this.subscriptions.add(
      this.dealService.getDealDetail(this.dealId).subscribe({
        next: d => {
          this.deal = d;
          this.loading = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.error = this.t('coSupply.loadError', 'Не удалось загрузить поставку');
          this.loading = false;
          this.cdr.markForCheck();
        }
      })
    );
  }

  /** Адрес поставки: по расчёту или по сделке. */
  get base(): (string | number)[] {
    return this.byDeal ? ['/cargo-owner/supplies', 'deal', this.dealId] : ['/cargo-owner/supplies', this.id];
  }

  open(tab: SupplyTab): void {
    this.router.navigate([...this.base, tab]);
  }

  /** Сделка поставки — для папок документов и перевозки. */
  get supplyDealId(): number | null {
    return this.byDeal ? this.dealId : (this.dealRequest?.dealId ?? null);
  }

  /** Маршрут и товары сделки — для шапки поставки без расчёта. */
  get dealRoute(): string {
    const r = this.deal?.cargoRequest;
    return r ? `${r.fromCity ?? ''} → ${r.toCity ?? ''}` : '';
  }

  get dealCargo(): string {
    const names = (this.deal?.cargoRequest?.products || []).map(p => p.name).filter((n): n is string => !!n);
    if (!names.length) return '';
    return names.length === 1 ? names[0] : `${names[0]} ${this.t('coDash.andMore', 'и ещё')} ${names.length - 1}`;
  }

  // ─── Предварительный расчёт: прогноз до поставки ───

  /** Прогноз доставки из расчёта (в валюте оценки); null — не рассчитывали. Авиа — без доставки после границы. */
  get forecastDelivery(): { amount: number; currency: string } | null {
    const c = this.calculation;
    if (!c || (c.freightAmount == null && c.freightAfterBorderAmount == null)) return null;
    const after = c.transportType === 'AIR' ? 0 : Number(c.freightAfterBorderAmount ?? 0);
    return { amount: Number(c.freightAmount ?? 0) + after, currency: c.freightCurrencyCode || 'USD' };
  }

  /** Доставка уже в цене товара (её доставляет продавец). */
  get sellerDelivers(): boolean {
    return this.calculation?.sellerDelivers === true;
  }

  /** Прогноз доставки в тенге по курсу на сегодня; 0 — доставка в цене товара; null — нет оценки или курса. */
  get forecastDeliveryKzt(): number | null {
    if (this.sellerDelivers) return 0;
    const d = this.forecastDelivery;
    if (!d) return null;
    if (d.currency === 'KZT') return d.amount;
    const rate = this.rates[d.currency];
    return rate ? d.amount * rate : null;
  }

  /** Ориентировочная стоимость поставки по расчёту: товар + платежи + прогноз доставки. */
  get forecastTotalKzt(): number {
    const c = this.calculation;
    return (c?.totalProductCostKzt || 0) + (c?.totalCustomsPayments || 0) + (this.forecastDeliveryKzt || 0);
  }

  get products() {
    return this.calculation?.products || [];
  }

  /** Разрешительные документы по кодам ТН ВЭД товаров; null — проверку ещё не запускали. */
  get permits(): { code: string; name: string; docs: string[] }[] | null {
    const c = this.calculation;
    const codes = Array.from(new Set(this.products.map(p => p.tnvedCodeForBroker).filter((x): x is string => !!x)));
    const lists = c?.certificateChecklists || [];
    if (!codes.length || !codes.some(code => lists.some(l => l.tnvedCode === code))) return null;
    return codes.map(code => {
      const l = lists.find(x => x.tnvedCode === code);
      return { code, name: l?.tnvedName || '', docs: (l?.requiredDocuments || []).map(d => d.name) };
    });
  }

  // ─── Сводка: прогноз против факта ───

  /** Факт по перевозке в тенге — выбранная ставка. */
  get actualFreightKzt(): number | null {
    return this.freightKzt;
  }

  /** Разница «факт − прогноз»: плюс — дороже прогноза. */
  diff(actual: number | null | undefined, forecast: number | null | undefined): number | null {
    return actual == null || forecast == null ? null : Math.round(actual - forecast);
  }

  // ─── Шапка ───

  get productsSummary(): string {
    const names = (this.calculation?.products || []).map(p => p.nameForBroker || p.nameOriginal).filter((n): n is string => !!n);
    if (!names.length) return this.calculation?.title || '';
    return names.length === 1 ? names[0] : `${names[0]} ${this.t('coDash.andMore', 'и ещё')} ${names.length - 1}`;
  }

  get statusText(): string {
    switch (this.calculation?.status) {
      case 'PROCESSED': return this.t('common.supplies.statusProcessed', 'Обработан');
      case 'CREATED': return this.t('common.supplies.statusCreated', 'Создан');
      default: return this.t('common.supplies.statusInProcessing', 'В обработке');
    }
  }

  // ─── Сводка ───

  money(v: number | null | undefined, currency = 'KZT'): string {
    if (v == null) return '—';
    const n = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: currency === 'KZT' ? 0 : 2 }).format(currency === 'KZT' ? Math.round(v) : v);
    return n + ' ' + (currency === 'KZT' ? '₸' : currency);
  }

  /** Заявка, по которой заключена сделка, и остальные заявки поставки. */
  get requests() {
    return this.calculation?.cargoRequests || [];
  }

  get dealRequest() {
    return this.requests.find(r => r.dealId);
  }

  /** Стоимость перевозки в тенге — по выбранной ставке. */
  get freightKzt(): number | null {
    const bid = this.deal?.bid;
    if (!bid) return null;
    return bid.priceKzt ?? (bid.currencyCode === 'KZT' ? bid.price : null);
  }

  /** Итого по поставке: товар + таможенные платежи + перевозка (если ставка уже выбрана). */
  get totalKzt(): number {
    const c = this.calculation;
    return (c?.totalProductCostKzt || 0) + (c?.totalCustomsPayments || 0) + (this.freightKzt || 0);
  }

  /** Текущий этап перевозки — последний статус сделки. */
  get dealStage(): string {
    const lang = this.languageService.getCurrentLanguageValue();
    const last = [...(this.deal?.activities || [])]
      .filter(a => (a.activityType as { category?: string })?.category === 'STATUS')
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
    if (!last) return this.t('coSupply.dealCreated', 'Сделка создана');
    return (lang === 'en' ? last.activityType.nameEn : last.activityType.nameRu) || '';
  }
}
